<script setup>
import { computed, shallowRef, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { statusColorClasses } from './constants/statusByCategory';
import { useCardDayDrag } from './composables/useCardDayDrag';

const props = defineProps({
  pipeline: { type: Object, required: true },
  cardsByColumn: { type: Function, required: true },
});

const emit = defineEmits(['cardClick', 'createCard']);

const { t } = useI18n();

const MAX_VISIBLE = 3;
const DAY_HEADERS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const currentDate = shallowRef(new Date());

function dateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const monthLabel = computed(() =>
  currentDate.value.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })
);

function goToPreviousMonth() {
  const d = new Date(currentDate.value);
  d.setMonth(d.getMonth() - 1);
  currentDate.value = d;
}

function goToNextMonth() {
  const d = new Date(currentDate.value);
  d.setMonth(d.getMonth() + 1);
  currentDate.value = d;
}

const weeks = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const start = new Date(firstDay);
  start.setDate(start.getDate() - startOffset);

  const cells = [];
  const cursor = new Date(start);
  while (cursor <= lastDay || cells.length % 7 !== 0) {
    cells.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (cells.length > 42) break;
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
});

const allCards = computed(() =>
  (props.pipeline.columns ?? []).flatMap(col => props.cardsByColumn(col.id))
);

function cardsForDay(day) {
  const key = dateKey(day);
  return allCards.value.filter(c => c.closing_date === key);
}

function isCurrentMonth(day) {
  return day.getMonth() === currentDate.value.getMonth();
}

function isToday(day) {
  return dateKey(day) === dateKey(new Date());
}

function cardColorClasses(card) {
  return statusColorClasses(props.pipeline.category, card.status);
}

const dayCellRefs = shallowRef(new Map());

function setDayCellRef(day, el) {
  const key = dateKey(day);
  if (el) {
    dayCellRefs.value.set(key, el);
  } else {
    dayCellRefs.value.delete(key);
  }
}

const { onCardMousedown, dragState } = useCardDayDrag({
  dayCellRefs,
  statusColorClasses: card => cardColorClasses(card),
});

function onDayClick(day) {
  const firstColumn = [...(props.pipeline.columns ?? [])].sort(
    (a, b) => a.position - b.position
  )[0];
  if (!firstColumn) return;
  emit('createCard', { columnId: firstColumn.id, closingDate: dateKey(day) });
}

// Popover de overflow: expande a célula ao passar o mouse sobre "+N mais",
// mostrando todos os cards do dia. Fecha ao sair do popover ou ao iniciar um drag.
const overflowDayKey = ref(null);
const overflowStyle = ref({});
let overflowCloseTimer = null;

function openOverflow(day, cellEl) {
  clearTimeout(overflowCloseTimer);
  const rect = cellEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const opensUp = spaceBelow < 200;

  overflowDayKey.value = dateKey(day);
  overflowStyle.value = opensUp
    ? {
        bottom: `${window.innerHeight - rect.bottom + rect.height}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minHeight: `${rect.height}px`,
      }
    : {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minHeight: `${rect.height}px`,
      };
}

function scheduleCloseOverflow() {
  clearTimeout(overflowCloseTimer);
  overflowCloseTimer = setTimeout(() => {
    overflowDayKey.value = null;
  }, 150);
}

function cancelCloseOverflow() {
  clearTimeout(overflowCloseTimer);
}

function onOverflowCardMousedown(event, card, cardEl) {
  onCardMousedown(event, card, cardEl);
  overflowDayKey.value = null;
}

const overflowCards = computed(() => {
  if (!overflowDayKey.value) return [];
  return allCards.value.filter(c => c.closing_date === overflowDayKey.value);
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden">
    <!-- Navegação de mês -->
    <div
      class="flex items-center justify-center gap-4 py-2 border-b border-n-weak flex-shrink-0"
    >
      <button
        class="i-lucide-chevron-left size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToPreviousMonth"
      />
      <span class="text-sm font-medium text-n-slate-12 min-w-40 text-center">
        {{ monthLabel }}
      </span>
      <button
        class="i-lucide-chevron-right size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToNextMonth"
      />
    </div>

    <!-- Cabeçalhos dos dias da semana -->
    <div class="grid grid-cols-7 border-b border-n-weak flex-shrink-0">
      <div
        v-for="day in DAY_HEADERS"
        :key="day"
        class="py-2 text-center text-xs font-medium text-n-slate-10 uppercase"
      >
        {{ day }}
      </div>
    </div>

    <!-- Grid das semanas -->
    <div class="flex-1 grid auto-rows-fr overflow-y-auto">
      <div
        v-for="(week, wi) in weeks"
        :key="wi"
        class="grid grid-cols-7 border-b border-n-weak last:border-b-0 min-h-[100px]"
      >
        <div
          v-for="day in week"
          :key="dateKey(day)"
          :ref="el => setDayCellRef(day, el)"
          data-day-cell
          class="border-r border-n-weak last:border-r-0 p-1 cursor-pointer hover:bg-n-alpha-1"
          :class="[
            !isCurrentMonth(day) ? 'bg-n-slate-3 text-n-slate-10' : '',
            dragState.targetDayKey === dateKey(day) ? 'bg-n-alpha-2' : '',
          ]"
          @click="onDayClick(day)"
        >
          <span
            class="text-xs font-medium inline-flex items-center justify-center size-6 rounded-full mb-1"
            :class="
              isToday(day)
                ? 'bg-n-brand text-white'
                : isCurrentMonth(day)
                  ? 'text-n-slate-12'
                  : 'text-n-slate-10'
            "
          >
            {{ day.getDate() }}
          </span>

          <!-- Chips de cards -->
          <div class="flex flex-col gap-0.5">
            <div
              v-for="card in cardsForDay(day).slice(0, MAX_VISIBLE)"
              :key="card.id"
              class="truncate rounded px-1 text-xs cursor-pointer text-white"
              :class="[
                cardColorClasses(card).bar,
                dragState.draggingId === card.id ? '!opacity-30' : '',
              ]"
              @click.stop="emit('cardClick', card)"
              @mousedown="onCardMousedown($event, card, $event.currentTarget)"
            >
              {{ card.title }}
            </div>
            <p
              v-if="cardsForDay(day).length > MAX_VISIBLE"
              class="text-xs text-n-slate-10 px-1 cursor-default"
              @mouseenter="
                openOverflow(
                  day,
                  $event.currentTarget.closest('[data-day-cell]')
                )
              "
              @mouseleave="scheduleCloseOverflow"
            >
              {{
                t('KANBAN.CALENDAR_VIEW.MORE', {
                  count: cardsForDay(day).length - MAX_VISIBLE,
                })
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="overflowDayKey"
      class="fixed z-[9998] bg-n-background border border-n-weak rounded-lg shadow-xl p-1 flex flex-col gap-0.5"
      :style="overflowStyle"
      @mouseenter="cancelCloseOverflow"
      @mouseleave="scheduleCloseOverflow"
    >
      <div
        v-for="card in overflowCards"
        :key="card.id"
        class="truncate rounded px-1 text-xs cursor-pointer text-white"
        :class="[
          cardColorClasses(card).bar,
          dragState.draggingId === card.id ? '!opacity-30' : '',
        ]"
        @click.stop="emit('cardClick', card)"
        @mousedown="onOverflowCardMousedown($event, card, $event.currentTarget)"
      >
        {{ card.title }}
      </div>
    </div>
  </Teleport>
</template>
